<template>
  <div
    :class="[
      'flex h-full flex-col rounded-2xl border p-4 sm:p-6',
      candidate.party === 'DEM' && 'border-democrat-subtle bg-democrat-subtle/20',
      candidate.party === 'GOP' && 'border-republican-subtle bg-republican-subtle/20',
      candidate.party === 'IND' && 'border-independent-subtle bg-independent-subtle/20',
      candidate.party === 'GRN' && 'border-green-subtle bg-green-subtle/20',
    ]"
  >
    <div class="mb-4 flex items-center justify-between gap-4">
      <div class="flex flex-col">
        <span
          :class="[
            'text-[8px] font-black uppercase tracking-tight sm:text-[10px]',
            candidate.party === 'DEM' && 'text-democrat/70',
            candidate.party === 'GOP' && 'text-republican/70',
            candidate.party === 'IND' && 'text-independent/70',
            candidate.party === 'GRN' && 'text-green/70',
          ]"
          >{{ t('chanceYes') }}</span
        >
        <div class="flex items-center gap-2">
          <!-- Loading spinner for price -->
          <div
            v-if="candidatePrice.isLoading"
            :class="[
              'h-3 w-3 animate-spin rounded-full border-2 border-transparent sm:h-4 sm:w-4',
              candidate.party === 'DEM' && 'border-l-democrat',
              candidate.party === 'GOP' && 'border-l-republican',
              candidate.party === 'IND' && 'border-l-independent',
              candidate.party === 'GRN' && 'border-l-green',
            ]"
          />
          <span
            :class="[
              'text-xl font-black sm:text-3xl',
              candidate.party === 'DEM' && 'text-democrat',
              candidate.party === 'GOP' && 'text-republican',
              candidate.party === 'IND' && 'text-independent',
              candidate.party === 'GRN' && 'text-green',
              candidatePrice.isLoading && 'opacity-50',
            ]"
            >{{ candidatePrice.price === null ? '\u2014' : `$${formatDollarPrice(candidatePrice.price)}` }}</span
          >
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-1 sm:gap-2">
        <Button
          unstyled
          :class="[
            'cursor-pointer rounded-lg sm:rounded-xl text-center font-black tabular-nums px-2 sm:px-3 py-1.5 sm:py-2 sm:text-sm shadow-md',
            hasNoPosition
              ? ['!bg-accent !text-on-accent', yesHovered && 'opacity-90']
              : [yesHovered ? '!bg-yes-subtle-hover !text-on-yes-subtle' : '!bg-yes-subtle !text-on-yes-subtle'],
          ]"
          @mouseenter="yesHovered = true"
          @mouseleave="yesHovered = false"
          @click="handleYesClick($event)"
        >
          <i v-if="hasNoPosition" class="ibi-close-x-filled mr-1 text-xs align-[-.1em]" />
          <template v-else>YES </template>
          {{ candidatePrice.price === null ? '—' : `$${formatDollarPrice(candidatePrice.price)}`
          }}<i v-if="!userState.authenticated" class="ibi-lock-closed ml-1 align-[-.1em]" />
        </Button>
        <Button
          unstyled
          :class="[
            'cursor-pointer rounded-lg sm:rounded-xl text-center font-black tabular-nums px-2 sm:px-3 py-1.5 sm:py-2 sm:text-sm shadow-md',
            hasYesPosition
              ? ['!bg-accent !text-on-accent', noHovered && 'opacity-90']
              : [noHovered ? '!bg-no-subtle-hover !text-on-no-subtle' : '!bg-no-subtle !text-on-no-subtle'],
          ]"
          @mouseenter="noHovered = true"
          @mouseleave="noHovered = false"
          @click="handleNoClick($event)"
        >
          <i v-if="hasYesPosition" class="ibi-close-x-filled mr-1 text-xs align-[-.1em]" />
          <template v-else>NO </template>
          {{ noPrice.price === null ? '—' : `$${formatDollarPrice(noPrice.price)}`
          }}<i v-if="!userState.authenticated" class="ibi-lock-closed ml-1 align-[-.1em]" />
        </Button>
      </div>
    </div>
    <div
      :class="[
        'mb-4 h-1.5 w-full overflow-hidden rounded-full sm:mb-6 sm:h-2',
        candidate.party === 'DEM' && 'bg-democrat-subtle',
        candidate.party === 'GOP' && 'bg-republican-subtle',
        candidate.party === 'IND' && 'bg-independent-subtle',
        candidate.party === 'GRN' && 'bg-green-subtle',
      ]"
    >
      <div
        :class="[
          'h-full transition-all duration-1000',
          candidate.party === 'DEM' && 'bg-democrat',
          candidate.party === 'GOP' && 'bg-republican',
          candidate.party === 'IND' && 'bg-independent',
          candidate.party === 'GRN' && 'bg-green',
        ]"
        :style="{ width: `${candidatePrice.price === null ? 0 : candidatePrice.price * 100}%` }"
      />
    </div>

    <!-- Desktop: exchange popover for multi-venue with position -->
    <StrikeExchangePopover
      ref="popoverRef"
      :contracts="popoverContracts"
      :strike-label="candidate.name"
      :side="popoverSide"
      :intent="popoverIntent"
      @buy-yes="handlePopoverBuyYes"
      @buy-no="handlePopoverBuyNo"
      @close-yes="handlePopoverCloseYes"
      @close-no="handlePopoverCloseNo"
    />

    <!-- Mobile: exchange drawer for multi-venue with position -->
    <StrikeExchangeDrawer
      v-model="drawerVisible"
      :strike-groups="drawerStrikeGroups"
      :market-name="candidate.name"
      expiration-label=""
      price-side="bid"
      :initial-strike="candidate.name"
      @buy-yes="handlePopoverBuyYes"
      @buy-no="handlePopoverBuyNo"
      @close-yes="handlePopoverCloseYes"
      @close-no="handlePopoverCloseNo"
    />

    <SpeedBumpDrawer
      v-model:visible="speedBump.visible.value"
      :config="speedBump.config.value"
      @action="speedBump.handleAction"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import Button from 'primevue/button';
import StrikeExchangePopover from '@/components/StrikeExchangePopover.vue';
import StrikeExchangeDrawer from '@/components/StrikeExchangeDrawer.vue';
import SpeedBumpDrawer from '@/components/SpeedBumpDrawer.vue';
import { useSpeedBump } from '@/composables/useSpeedBump';
import type { ActualCandidate } from '../types/election.types';
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { StrikeGroup } from '@/types/contracts.types';
import { useGetEventContractBidAsk, useGetFlexbestBidAsk } from '@/api/mdfarm.queries';
import { formatDollarPrice } from '@/utils/formatPrice';
import { resolveCloseConid } from '@/utils/closeConid';
import { useOrderTicket } from '../composables/useOrderTicket';
import { useUserState } from '@/stores/useUserState';
import { usePositions } from '@/stores/usePositions';
import { hasPositionInPairs } from '../utils/matchedConids';

interface Props {
  candidate: ActualCandidate;
}

const props = defineProps<Props>();
const { t } = useI18n();

const { openOrderTicket } = useOrderTicket();
const userState = useUserState();
const positionsStore = usePositions();
const speedBump = useSpeedBump();

// A position may be held on any matched exchange, so check every yes/no conid
// carried in the candidate's conid@exchange pairs, not just the primary contract.
const hasYesPosition = computed(() => {
  return hasPositionInPairs(props.candidate.yesConidExPairs, props.candidate.conid, positionsStore.positionDataMap);
});

const hasNoPosition = computed(() => {
  return hasPositionInPairs(props.candidate.noConidExPairs, props.candidate.conid_no, positionsStore.positionDataMap);
});

const isDesktopPopover = useMediaQuery('(min-width: 1280px)');
const isMultiExchange = computed(() => (props.candidate.yesConidExPairs?.length ?? 0) > 1);
const hasPosition = computed(() => hasYesPosition.value || hasNoPosition.value);

const popoverRef = ref<InstanceType<typeof StrikeExchangePopover> | null>(null);
const popoverSide = ref<'yes' | 'no'>('yes');
const popoverIntent = ref<string | undefined>();
const drawerVisible = ref(false);
const yesHovered = ref(false);
const noHovered = ref(false);

const popoverContracts = computed<PredictionContractListContract[]>(() => {
  const yesPairs = props.candidate.yesConidExPairs ?? [];
  const noPairs = props.candidate.noConidExPairs ?? [];
  return yesPairs.map((yp, i) => {
    const [yesConid, exchange] = yp.split('@');
    const [noConid] = (noPairs[i] ?? '').split('@');
    return {
      conid_yes: Number(yesConid),
      conid_no: Number(noConid),
      exchange,
      strike_label: props.candidate.name,
      underlying_conid: props.candidate.conid,
      product_conid: props.candidate.conid,
    };
  });
});

const drawerStrikeGroups = computed<StrikeGroup[]>(() => {
  if (!popoverContracts.value.length) return [];
  return [
    {
      strike_label: props.candidate.name,
      primary: popoverContracts.value[0],
      contracts: popoverContracts.value,
      exchanges: popoverContracts.value.map((c) => c.exchange),
      expandable: popoverContracts.value.length > 1,
    },
  ];
});

function handleYesClick(event: Event) {
  if (isMultiExchange.value && hasPosition.value) {
    popoverSide.value = 'yes';
    popoverIntent.value = hasNoPosition.value ? 'close' : undefined;
    if (isDesktopPopover.value) {
      popoverRef.value?.toggle(event);
    } else {
      drawerVisible.value = true;
    }
    return;
  }
  const exchange = isMultiExchange.value ? 'BEST' : props.candidate.exchange;
  const closeConid = hasNoPosition.value
    ? resolveCloseConid({
        exchange,
        forecastxConid: props.candidate.conid,
        defaultConid: props.candidate.conid_no,
      })
    : props.candidate.conid;
  openOrderTicket(closeConid, hasNoPosition.value ? 'close' : undefined, exchange, props.candidate.yesConidExPairs);
}

function handleNoClick(event: Event) {
  if (isMultiExchange.value && hasPosition.value) {
    popoverSide.value = 'no';
    popoverIntent.value = hasYesPosition.value ? 'close' : undefined;
    if (isDesktopPopover.value) {
      popoverRef.value?.toggle(event);
    } else {
      drawerVisible.value = true;
    }
    return;
  }
  const exchange = isMultiExchange.value ? 'BEST' : props.candidate.exchange;
  const closeConid = hasYesPosition.value
    ? resolveCloseConid({
        exchange,
        forecastxConid: props.candidate.conid_no,
        defaultConid: props.candidate.conid,
      })
    : props.candidate.conid_no;
  openOrderTicket(closeConid, hasYesPosition.value ? 'close' : undefined, exchange, props.candidate.noConidExPairs);
}

function handlePopoverBuyYes(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const group = drawerStrikeGroups.value[0];
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'yes', conid: contract.conid_yes, eventConidExs })
  ) {
    drawerVisible.value = false;
    return;
  }
  openOrderTicket(contract.conid_yes, undefined, exchange, eventConidExs);
  drawerVisible.value = false;
}

function handlePopoverBuyNo(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const group = drawerStrikeGroups.value[0];
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'no', conid: contract.conid_no, eventConidExs })
  ) {
    drawerVisible.value = false;
    return;
  }
  openOrderTicket(contract.conid_no, undefined, exchange, eventConidExs);
  drawerVisible.value = false;
}

function handlePopoverCloseYes(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_yes,
    defaultConid: contract.conid_no,
  });
  openOrderTicket(conid, 'close', exchange, eventConidExs);
  drawerVisible.value = false;
}

function handlePopoverCloseNo(contract: PredictionContractListContract, exchange?: string, eventConidExs?: string[]) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_no,
    defaultConid: contract.conid_yes,
  });
  openOrderTicket(conid, 'close', exchange, eventConidExs);
  drawerVisible.value = false;
}

const useYesFlexbest = computed(() => (props.candidate.yesConidExPairs?.length ?? 0) > 1);
const useNoFlexbest = computed(() => (props.candidate.noConidExPairs?.length ?? 0) > 1);

const yesSingleQuery = useGetEventContractBidAsk(
  computed(() => (!useYesFlexbest.value ? props.candidate.conid : undefined)),
  () => props.candidate.exchange,
);
const yesFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useYesFlexbest.value ? props.candidate.yesConidExPairs : undefined)),
);

const noSingleQuery = useGetEventContractBidAsk(
  computed(() => (!useNoFlexbest.value ? props.candidate.conid_no : undefined)),
  () => props.candidate.exchange,
);
const noFlexbestQuery = useGetFlexbestBidAsk(
  computed(() => (useNoFlexbest.value ? props.candidate.noConidExPairs : undefined)),
);

const yesData = computed(() => (useYesFlexbest.value ? yesFlexbestQuery.data.value : yesSingleQuery.data.value));
const noData = computed(() => (useNoFlexbest.value ? noFlexbestQuery.data.value : noSingleQuery.data.value));

const candidatePrice = computed(() => ({
  price: yesData.value?.bid ?? null,
  isLoading: useYesFlexbest.value ? yesFlexbestQuery.isLoading.value : yesSingleQuery.isLoading.value,
}));

const noPrice = computed(() => ({
  price: noData.value?.bid ?? null,
  isLoading: useNoFlexbest.value ? noFlexbestQuery.isLoading.value : noSingleQuery.isLoading.value,
}));
</script>
<!-- last modified by AI tools on 2026-10-09 -->
